import * as THREE from 'three';
import type { ShortcutStyle } from '@/config/cities';
import type { Track, TrackNode } from '@/world/Track';
import type { TrackTheme } from '@/world/Theme';
import { tuning } from '@/config/tuning';
import type { PlacedShortcut, KickerFrame } from '@/physics/shortcuts';
import { buildTokyoShortcut } from '@/world/scenery/shortcutTokyo';
import { buildNapoliShortcut } from '@/world/scenery/shortcutNapoli';

/**
 * shortcutMeshes.ts — the VISUALS for the SECRET SHORTCUTS (M117c aerial
 * rework): every structure is built from the SAME `placeShortcuts` table the
 * physics drives, so the ramp you see IS the launcher and the deck you see IS
 * the channel you land on (the M47 what-you-see-is-what-collides rule).
 *
 * Batch-1 builds (this file):
 *  - 'cavern' (Rio): chevron kicker → rock cliff wearing a dark mouth up
 *    high → granite gallery → open sand chute back down.
 *  - 'highline' (NYC): a bigger chevron park ramp → an elevated concrete
 *    line with planter edges + steel columns, riding beside the towers →
 *    steel descent chute to a gap at road grade.
 *  - 'skypath' (Lagos): the city's own enlarged jetty ramp is the launcher;
 *    a timber deck on stilts hangs past its lip (cantilevered over the
 *    road), swings out over the lagoon, and descends back in.
 *
 * Batch-2 builds live in THEIR OWN FILES so each city's design agent works
 * without merge risk: `sc.look` picks 'expressway' → shortcutTokyo.ts and
 * 'rooftop' → shortcutNapoli.ts (look is VISUAL identity only, resolved by
 * placeShortcuts; physics per style is byte-identical). The shared geometry
 * helpers below (chStrip / wallBand / glowStrips / box / deckStructure /
 * dressExit / buildKicker / textures) are exported for those builders.
 *
 * The CHEVRON DECK is the series' entry language now: spot the faded
 * red/yellow ramp, and you know a flight lives here. All materials
 * self-contained; swappable in the Design pass.
 */

export interface Palette {
  wall: number;
  floor: number;
  trim: number;
  /** Glow accent (rail lights / lamps / light shafts). */
  accent: number;
}

export const PALETTES: Record<ShortcutStyle, Palette> = {
  cavern: { wall: 0x6a635b, floor: 0xc8a468, trim: 0x3e3833, accent: 0x9fe8ff }, // Tijuca granite over sand, skylight blue
  highline: { wall: 0x9aa0a4, floor: 0xb9bcb4, trim: 0x2e3134, accent: 0xffd9a0 }, // pale concrete, dark steel, warm park lamps
  skypath: { wall: 0x9a6b3a, floor: 0x8a5f34, trim: 0x5a3f26, accent: 0xffce7a }, // weathered lagoon timber, warm lanterns
};

/** Cavern interior height — the chase camera rides ~4.8 above the car. */
const INTERIOR_H = (): number => tuning.shortcuts.interiorH;
const RAIL_H = 1.05;

export function mat(color: number, rough = 0.9, emissive = 0, eIntensity = 0): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.04 });
  if (emissive) {
    m.emissive = new THREE.Color(emissive);
    m.emissiveIntensity = eIntensity;
  }
  return m;
}

/** Deterministic 0..1 hash for canvas/vertex grain (never the layout RNG). */
export function art(i: number): number {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

// ---- Canvas painters ------------------------------------------------------

export function canvasTex(w: number, h: number, paint: (x: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  paint(x);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Grey granite: mottled facets + cracks + moss (fine-grained — magnified
 *  cracks read as planks, measured). */
function rockTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 64, (x) => {
    x.fillStyle = '#6a635b';
    x.fillRect(0, 0, 128, 64);
    for (let i = 0; i < 40; i++) {
      const g2 = 84 + art(i) * 52;
      x.fillStyle = `rgba(${g2},${g2 - 4 - art(i * 3) * 8},${g2 - 10 - art(i * 5) * 10},${0.2 + art(i * 7) * 0.24})`;
      x.fillRect(art(i * 11) * 128, art(i * 13) * 64, 8 + art(i * 17) * 30, 4 + art(i * 19) * 12);
    }
    for (let i = 0; i < 8; i++) {
      x.fillStyle = `rgba(${52 + art(i * 41) * 30},${86 + art(i * 43) * 40},${44 + art(i * 47) * 24},${0.16 + art(i * 53) * 0.12})`;
      x.fillRect(art(i * 59) * 128, art(i * 61) * 64, 5 + art(i * 67) * 14, 3 + art(i * 71) * 7);
    }
    x.strokeStyle = 'rgba(30,27,24,0.3)';
    for (let i = 0; i < 9; i++) {
      x.lineWidth = 1;
      x.beginPath();
      x.moveTo(art(i * 29) * 128, 0);
      x.lineTo(art(i * 29) * 128 + (art(i * 31) - 0.5) * 50, 64);
      x.stroke();
    }
  });
  tex.repeat.set(3, 1);
  return tex;
}

/** High-Line deck: the park's precast concrete PLANKS — long boards running
 *  with the deck, tapered joints, worn tracks where feet (and now tyres) go.
 *  v spans ACROSS the deck.
 *
 *  shared-pass: the painted "planter greens" that used to bleed in along v 0-8
 *  and 56-64 are GONE. They were a flat green stripe standing in for planting,
 *  and on a deck the camera actually rides they read as paint, not plants. The
 *  planting is real geometry now (buildHighline's planter beds sit OUTBOARD of
 *  this running surface), so the deck texture is just the deck. */
export function concreteDeckTexture(): THREE.CanvasTexture {
  return canvasTex(128, 64, (x) => {
    x.fillStyle = '#b9bcb4';
    x.fillRect(0, 0, 128, 64);
    for (let i = 0; i < 26; i++) {
      const g2 = 168 + art(i) * 26;
      x.fillStyle = `rgba(${g2},${g2 + 2},${g2 - 6},${0.18 + art(i * 7) * 0.2})`;
      x.fillRect(art(i * 11) * 128, art(i * 13) * 64, 6 + art(i * 17) * 22, 4 + art(i * 19) * 10);
    }
    // Plank joints ALONG the deck (v = across), the High Line's real paving.
    x.strokeStyle = 'rgba(88,92,88,0.55)';
    x.lineWidth = 1;
    for (let gy = 8; gy < 64; gy += 8) {
      x.beginPath();
      x.moveTo(0, gy + 0.5);
      x.lineTo(128, gy + 0.5);
      x.stroke();
    }
    // Cross joints, staggered per plank row so it never rules a grid.
    x.strokeStyle = 'rgba(96,100,96,0.4)';
    for (let row = 0; row < 8; row++) {
      const off = art(row * 7) * 32;
      for (let gx = off; gx <= 128; gx += 42) {
        x.beginPath();
        x.moveTo(gx + 0.5, row * 8);
        x.lineTo(gx + 0.5, row * 8 + 8);
        x.stroke();
      }
    }
    // Worn wheel tracks either side of the crown + moss creeping in at the
    // plank joints nearest the planting.
    for (const t of [18, 44]) {
      x.fillStyle = 'rgba(126,128,122,0.22)';
      x.fillRect(0, t, 128, 6);
    }
    for (let i = 0; i < 18; i++) {
      const edge = art(i * 23) < 0.5 ? 1 : 57;
      x.fillStyle = `rgba(${72 + art(i * 5) * 30},${96 + art(i * 9) * 34},${58 + art(i * 13) * 22},${0.25 + art(i * 3) * 0.3})`;
      x.fillRect(art(i * 11) * 128, edge + art(i * 17) * 4, 4 + art(i * 19) * 9, 2);
    }
  });
}

/** Weathered structural face for a deck's girder / soffit: cast panels with
 *  form-tie marks, wide joints and rust bleed. `jointEvery` px = one panel. */
function deckStructTexture(base: string, rust: number, jointEvery = 42): THREE.CanvasTexture {
  return canvasTex(128, 32, (x) => {
    x.fillStyle = base;
    x.fillRect(0, 0, 128, 32);
    for (let i = 0; i < 16; i++) {
      const g2 = 108 + art(i * 3) * 46;
      x.fillStyle = `rgba(${g2},${g2 + 3},${g2 + 9},${0.07 + art(i * 5) * 0.11})`;
      x.fillRect(art(i * 7) * 128, art(i * 9) * 32, 8 + art(i * 11) * 22, 4 + art(i * 13) * 11);
    }
    // Panel joints — WIDE (a tight pitch stripes the whole span from the road
    // below; the Tokyo viaduct measured this and opened its joints to 3 u).
    x.strokeStyle = 'rgba(24,26,31,0.45)';
    x.lineWidth = 1;
    for (let gx = 0; gx <= 128; gx += jointEvery) {
      x.beginPath();
      x.moveTo(gx + 0.5, 0);
      x.lineTo(gx + 0.5, 32);
      x.stroke();
    }
    // Form-tie dimples in a row down each panel.
    x.fillStyle = 'rgba(30,32,38,0.5)';
    for (let gx = jointEvery / 2; gx < 128; gx += jointEvery) {
      for (const gy of [9, 22]) x.fillRect(gx - 1, gy, 2, 2);
    }
    // Rust bleed running down from the ties + a dirt line along the bottom.
    for (let i = 0; i < rust; i++) {
      const sx = art(i * 17) * 128;
      const h = 6 + art(i * 19) * 20;
      x.fillStyle = `rgba(${104 + art(i * 23) * 54},${58 + art(i * 29) * 26},${30 + art(i * 31) * 14},${0.14 + art(i * 37) * 0.2})`;
      x.fillRect(sx, art(i * 41) * 8, 1.5 + art(i * 43) * 2.5, h);
    }
    x.fillStyle = 'rgba(14,15,20,0.3)';
    x.fillRect(0, 28, 128, 4);
  });
}

/** Prairie planting for the High Line's beds: a tuft of grass blades + a few
 *  seed heads on a transparent field (crossed billboards, alphaTest). */
function plantingTexture(): THREE.CanvasTexture {
  const tex = canvasTex(64, 64, (x) => {
    x.clearRect(0, 0, 64, 64);
    for (let i = 0; i < 26; i++) {
      const bx = 4 + art(i * 7) * 56;
      const h = 26 + art(i * 11) * 34;
      const lean = (art(i * 13) - 0.5) * 16;
      const g2 = 96 + art(i * 17) * 60;
      x.strokeStyle = `rgba(${52 + art(i * 19) * 44},${g2},${44 + art(i * 23) * 30},0.96)`;
      x.lineWidth = 1.6 + art(i * 29) * 1.8;
      x.beginPath();
      x.moveTo(bx, 64);
      x.quadraticCurveTo(bx + lean * 0.4, 64 - h * 0.6, bx + lean, 64 - h);
      x.stroke();
      // Tawny seed head on the taller blades — the High Line reads gold in
      // late season, and pure green alone looks like a hedge.
      if (h > 44) {
        x.fillStyle = `rgba(${188 + art(i * 31) * 40},${156 + art(i * 37) * 40},${88 + art(i * 41) * 30},0.9)`;
        x.fillRect(bx + lean - 1.4, 64 - h - 3, 2.8, 7);
      }
    }
  });
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** Weathered timber planks (Lagos jetty language). v spans ACROSS the deck.
 *  Exported so the city's jetty RAMP wears the same boards as the sky-path
 *  it launches you onto (M117d — one timber language on the causeway). */
export function plankTexture(): THREE.CanvasTexture {
  return canvasTex(128, 64, (x) => {
    x.fillStyle = '#8a5f34';
    x.fillRect(0, 0, 128, 64);
    for (let row = 0; row < 8; row++) {
      const y0 = row * 8;
      const off = (row % 2) * 10;
      x.fillStyle = `rgba(${120 + art(row) * 40},${80 + art(row * 3) * 30},${44 + art(row * 5) * 20},0.55)`;
      x.fillRect(0, y0, 128, 7);
      x.strokeStyle = 'rgba(60,40,22,0.7)';
      x.lineWidth = 1;
      x.beginPath();
      x.moveTo(0, y0 + 7.5);
      x.lineTo(128, y0 + 7.5);
      x.stroke();
      for (let gx = -20; gx <= 128; gx += 40) {
        x.beginPath();
        x.moveTo(gx + off + 0.5, y0);
        x.lineTo(gx + off + 0.5, y0 + 7);
        x.stroke();
      }
    }
  });
}

/** The launcher deck skin (M117c, quieter per the owner): weathered timber
 *  with chevron GHOSTS worn nearly away, plus a slim boost strip up the
 *  middle — the ramp reads as course furniture with a go-fast cue, not a
 *  signposted secret. */
function launcherTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.fillStyle = '#7c5a36';
    x.fillRect(0, 0, 64, 64);
    for (let row = 0; row < 8; row++) {
      x.fillStyle = `rgba(${110 + art(row) * 34},${76 + art(row * 3) * 26},${42 + art(row * 5) * 18},0.6)`;
      x.fillRect(0, row * 8, 64, 7);
      x.strokeStyle = 'rgba(52,36,20,0.65)';
      x.lineWidth = 1;
      x.beginPath();
      x.moveTo(0, row * 8 + 7.5);
      x.lineTo(64, row * 8 + 7.5);
      x.stroke();
    }
    // Chevron ghosts: old paint, mostly weathered off.
    x.globalAlpha = 0.16;
    x.fillStyle = '#d8b93c';
    for (let i = -2; i < 6; i++) {
      x.beginPath();
      x.moveTo(-16 + i * 24, 64);
      x.lineTo(8 + i * 24, 0);
      x.lineTo(20 + i * 24, 0);
      x.lineTo(-4 + i * 24, 64);
      x.closePath();
      x.fill();
    }
    x.globalAlpha = 1;
    // The boost strip: a slim cool-glow band up the centre (the M99 pad
    // language, shrunk to a cue).
    const g = x.createLinearGradient(26, 0, 38, 0);
    g.addColorStop(0, 'rgba(80,200,255,0)');
    g.addColorStop(0.5, 'rgba(120,225,255,0.55)');
    g.addColorStop(1, 'rgba(80,200,255,0)');
    x.fillStyle = g;
    x.fillRect(24, 0, 16, 64);
  });
}

/** A laid STEEL ROAD PLATE (M117d, owner: the NYC launcher should "look like
 *  an obstacle construction zone but operate like a ramp"): diamond tread,
 *  rust bloom, weld beads and bolt holes.
 *  M117f — the hazard paint used to run as two UNBROKEN yellow lines up both
 *  edges, which read at speed as runway edge markings (measured on the
 *  approach: the plate's two brightest features were those lines and the old
 *  centre strip). It is scraps of worn paint now, and mud smears break the
 *  tread up so the sheet reads used rather than laid out. */
function steelPlateTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.fillStyle = '#55585e';
    x.fillRect(0, 0, 64, 64);
    // Diamond tread, LOW contrast (a bright grid reads as painted graphics).
    x.strokeStyle = 'rgba(122,128,136,0.24)';
    x.lineWidth = 2;
    for (let i = -64; i < 64; i += 11) {
      x.beginPath();
      x.moveTo(i, 0);
      x.lineTo(i + 64, 64);
      x.stroke();
      x.beginPath();
      x.moveTo(i + 64, 0);
      x.lineTo(i, 64);
      x.stroke();
    }
    // Rust bloom.
    for (let i = 0; i < 26; i++) {
      x.fillStyle = `rgba(${118 + art(i) * 60},${68 + art(i * 3) * 30},${32 + art(i * 5) * 18},${0.1 + art(i * 7) * 0.22})`;
      x.fillRect(art(i * 11) * 64, art(i * 13) * 64, 3 + art(i * 17) * 12, 2 + art(i * 19) * 8);
    }
    // Weld beads across the sheet + bolt holes at the corners.
    for (const wy of [17, 45]) {
      x.fillStyle = 'rgba(150,154,160,0.3)';
      for (let gx = 0; gx < 64; gx += 3) x.fillRect(gx, wy + (art(gx) - 0.5) * 1.6, 2, 1.4);
    }
    x.fillStyle = 'rgba(22,23,26,0.75)';
    for (const [bx, by] of [
      [6, 6],
      [57, 7],
      [6, 56],
      [56, 57],
      [31, 31],
    ] as const) {
      x.beginPath();
      x.arc(bx, by, 1.5, 0, Math.PI * 2);
      x.fill();
    }
    // Scraps of old hazard paint — never a continuous edge line.
    x.globalAlpha = 0.26;
    x.fillStyle = '#c9ab3c';
    x.fillRect(0, 6, 5, 15);
    x.fillRect(0, 40, 4, 9);
    x.fillRect(60, 24, 4, 21);
    x.globalAlpha = 1;
    // Dried mud dragged across it — the tread only shows where it is clean.
    for (let i = 0; i < 12; i++) {
      x.fillStyle = `rgba(${62 + art(i * 23) * 30},${52 + art(i * 29) * 22},${38 + art(i * 31) * 16},${0.18 + art(i * 37) * 0.3})`;
      x.beginPath();
      x.ellipse(art(i * 41) * 64, art(i * 43) * 64, 6 + art(i * 47) * 16, 4 + art(i * 53) * 9, art(i * 59) * 3, 0, Math.PI * 2);
      x.fill();
    }
  });
}

/** A RAGGED excavation cut (M117f): transparent outside a broken outline, so
 *  the decal has no rectangle to give it away — inside, broken sub-base rim,
 *  a dark void and a length of exposed duct. Flat on the road = nothing to
 *  hit (the M117d trench rule). */
export function trenchTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 64, (x) => {
    x.clearRect(0, 0, 128, 64);
    const ring = (rBase: number, seed: number): void => {
      x.beginPath();
      const N = 30;
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * Math.PI * 2;
        const r = rBase + (art(i * 13 + seed) - 0.5) * 0.26;
        const px = 64 + Math.cos(a) * 61 * r;
        const py = 32 + Math.sin(a) * 29 * r;
        if (i === 0) x.moveTo(px, py);
        else x.lineTo(px, py);
      }
      x.closePath();
    };
    // Broken asphalt / sub-base rim.
    ring(0.94, 3);
    x.fillStyle = '#4c463d';
    x.fill();
    x.save();
    x.clip();
    for (let i = 0; i < 22; i++) {
      x.fillStyle = `rgba(${58 + art(i * 7) * 44},${52 + art(i * 11) * 36},${42 + art(i * 13) * 26},0.6)`;
      x.fillRect(art(i * 17) * 128, art(i * 19) * 64, 4 + art(i * 23) * 12, 3 + art(i * 29) * 8);
    }
    x.restore();
    // The void.
    ring(0.68, 41);
    x.fillStyle = '#131417';
    x.fill();
    x.save();
    x.clip();
    // A length of duct lying in the cut + loose spoil at one end.
    x.fillStyle = '#5a5f64';
    x.fillRect(24, 28, 62, 7);
    x.fillStyle = 'rgba(20,22,26,0.55)';
    x.fillRect(24, 33, 62, 3);
    x.fillStyle = '#3c352c';
    x.fillRect(84, 20, 26, 22);
    x.restore();
  });
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** Mud tracked out of a site by plant tyres: a fading fan with two tread
 *  lines. Canvas y = away from the works (0 = the dirty end). */
function trackedDirtTexture(): THREE.CanvasTexture {
  const tex = canvasTex(64, 128, (x) => {
    x.clearRect(0, 0, 64, 128);
    const g = x.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, 'rgba(74,62,46,0.7)');
    g.addColorStop(0.45, 'rgba(74,62,46,0.32)');
    g.addColorStop(1, 'rgba(74,62,46,0)');
    x.fillStyle = g;
    x.beginPath();
    x.moveTo(4, 0);
    x.lineTo(60, 0);
    x.lineTo(48, 128);
    x.lineTo(16, 128);
    x.closePath();
    x.fill();
    // Two tread lines with cleat marks, fading out with the fan.
    for (const cx of [22, 42]) {
      for (let i = 0; i < 26; i++) {
        const t = i / 26;
        x.fillStyle = `rgba(46,38,28,${0.6 * (1 - t)})`;
        x.fillRect(cx - 4 + (t - 0.5) * (cx - 32) * 0.5, i * 5 + art(i * 7) * 2, 8, 2.4);
      }
    }
    for (let i = 0; i < 26; i++) {
      x.fillStyle = `rgba(58,48,36,${0.5 * (1 - i / 26)})`;
      x.fillRect(art(i * 11) * 56 + 4, art(i * 13) * 110, 2 + art(i * 17) * 5, 2 + art(i * 19) * 4);
    }
    // Feather BOTH sides last — a hard trapezoid edge on the road reads as a
    // painted rectangle from a low 3/4 angle (measured).
    const side = x.createLinearGradient(0, 0, 64, 0);
    side.addColorStop(0, 'rgba(0,0,0,1)');
    side.addColorStop(0.3, 'rgba(0,0,0,0)');
    side.addColorStop(0.7, 'rgba(0,0,0,0)');
    side.addColorStop(1, 'rgba(0,0,0,1)');
    x.globalCompositeOperation = 'destination-out';
    x.fillStyle = side;
    x.fillRect(0, 0, 64, 128);
    x.globalCompositeOperation = 'source-over';
  });
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** A whole site FENCE PANEL (frame + mesh) on one transparent canvas — the
 *  panel is a single plane, so a long run costs one instanced draw call.
 *  Exported: the works-site language is shared across cities. */
export function fencePanelTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 96, (x) => {
    x.clearRect(0, 0, 128, 96);
    // Diamond mesh.
    x.strokeStyle = 'rgba(176,184,192,0.85)';
    x.lineWidth = 1.4;
    for (let i = -96; i < 128; i += 9) {
      x.beginPath();
      x.moveTo(i, 0);
      x.lineTo(i + 96, 96);
      x.stroke();
      x.beginPath();
      x.moveTo(i + 96, 0);
      x.lineTo(i, 96);
      x.stroke();
    }
    // Tube frame.
    x.strokeStyle = '#c8cdd2';
    x.lineWidth = 6;
    x.strokeRect(3, 3, 122, 90);
    x.strokeStyle = 'rgba(120,128,136,0.9)';
    x.lineWidth = 4;
    x.beginPath();
    x.moveTo(64, 3);
    x.lineTo(64, 93);
    x.stroke();
  });
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** Site hoarding: plywood panels over a hazard-striped kick board. */
function hoardingTexture(): THREE.CanvasTexture {
  return canvasTex(128, 64, (x) => {
    x.fillStyle = '#b3813f';
    x.fillRect(0, 0, 128, 64);
    for (let i = 0; i < 20; i++) {
      x.fillStyle = `rgba(${150 + art(i) * 50},${110 + art(i * 3) * 34},${58 + art(i * 5) * 24},${0.2 + art(i * 7) * 0.22})`;
      x.fillRect(art(i * 11) * 128, art(i * 13) * 64, 8 + art(i * 17) * 26, 4 + art(i * 19) * 12);
    }
    x.strokeStyle = 'rgba(90,62,28,0.8)';
    x.lineWidth = 2;
    for (let gx = 0; gx <= 128; gx += 32) {
      x.beginPath();
      x.moveTo(gx + 1, 0);
      x.lineTo(gx + 1, 64);
      x.stroke();
    }
    // Hazard kick board along the bottom.
    x.fillStyle = '#e2b02a';
    x.fillRect(0, 46, 128, 18);
    x.fillStyle = '#2b2b2e';
    for (let i = -2; i < 14; i++) {
      x.beginPath();
      x.moveTo(i * 12, 64);
      x.lineTo(i * 12 + 9, 46);
      x.lineTo(i * 12 + 16, 46);
      x.lineTo(i * 12 + 7, 64);
      x.closePath();
      x.fill();
    }
  });
}

/** The cavern mouth face: rock with a ragged dark opening (alphaTest hole). */
function mouthFaceTexture(): THREE.CanvasTexture {
  return canvasTex(128, 128, (x) => {
    x.fillStyle = '#6a635b';
    x.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 40; i++) {
      const g2 = 84 + art(i) * 52;
      x.fillStyle = `rgba(${g2},${g2 - 6},${g2 - 14},${0.2 + art(i * 7) * 0.22})`;
      x.fillRect(art(i * 11) * 128, art(i * 13) * 128, 10 + art(i * 17) * 30, 6 + art(i * 19) * 14);
    }
    x.save();
    x.globalCompositeOperation = 'destination-out';
    x.beginPath();
    x.moveTo(24, 128);
    x.lineTo(24, 60);
    for (let i = 0; i <= 10; i++) {
      x.lineTo(24 + (i / 10) * 80, 60 - Math.sin((i / 10) * Math.PI) * 34 + (art(i * 37) - 0.5) * 12);
    }
    x.lineTo(104, 128);
    x.closePath();
    x.fillStyle = '#000';
    x.fill();
    x.restore();
  });
}

// ---- Geometry helpers -----------------------------------------------------

export type ChannelTrack = PlacedShortcut['channel'];

/** A quad strip along CHANNEL nodes (UV v runs one repeat per ~uvStep units). */
export function chStrip(
  nodes: readonly TrackNode[],
  step: number,
  from: number,
  to: number,
  vertsAt: (nd: TrackNode, i: number) => [THREE.Vector3, THREE.Vector3],
  material: THREE.Material,
  uvStep = 7,
): THREE.Mesh {
  const lo = Math.max(0, from);
  const hi = Math.min(nodes.length - 1, to);
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let i = lo; i <= hi; i++) {
    const [a, b] = vertsAt(nodes[i], i);
    pos.push(a.x, a.y, a.z, b.x, b.y, b.z);
    const v = (i * step) / uvStep;
    uv.push(v, 0, v, 1);
    if (i > lo) {
      const k = (i - lo) * 2;
      idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  const mesh = new THREE.Mesh(g, material);
  mesh.matrixAutoUpdate = false;
  return mesh;
}

/** A band along the MAIN track's wall line between two arcs (cliff faces). */
export function wallBand(
  track: Track,
  side: -1 | 1,
  fromArc: number,
  toArc: number,
  off: number,
  yBot: (t: number, grade: number) => number,
  yTop: (t: number, grade: number) => number,
  material: THREE.Material,
  uvRepeat = 6,
): THREE.Mesh {
  const n = Math.max(2, Math.ceil((toArc - fromArc) / 2.2));
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const s = track.sampleAt(fromArc + (toArc - fromArc) * t);
    const x = s.x + s.rightX * side * (track.halfWidth + off);
    const z = s.z + s.rightZ * side * (track.halfWidth + off);
    pos.push(x, yBot(t, s.y), z, x, yTop(t, s.y), z);
    uv.push(t * uvRepeat, 0, t * uvRepeat, 1);
    if (i > 0) {
      const k = i * 2;
      idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  const mesh = new THREE.Mesh(g, material);
  mesh.matrixAutoUpdate = false;
  return mesh;
}

export function nodeAt(ch: ChannelTrack, d: number): number {
  return Math.max(0, Math.min(ch.nodes.length - 1, Math.round(d / ch.step)));
}

/** Thin additive glow strip along both deck edges (night readability). */
export function glowStrips(
  g: THREE.Group,
  ch: ChannelTrack,
  from: number,
  to: number,
  half: number,
  height: number,
  color: number,
): void {
  const m = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, from, to, (nd) => {
        const x = nd.x + nd.rightX * s * half;
        const z = nd.z + nd.rightZ * s * half;
        return [new THREE.Vector3(x, nd.y + height, z), new THREE.Vector3(x, nd.y + height + 0.13, z)];
      }, m),
    );
  }
}

/** A box helper oriented by heading (§6.1: mesh fronting +Z → rot.y = h). */
export function box(
  w: number,
  h: number,
  d: number,
  material: THREE.Material,
  x: number,
  y: number,
  z: number,
  heading = 0,
  pitch = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  m.position.set(x, y, z);
  m.rotation.order = 'YXZ';
  m.rotation.y = heading;
  m.rotation.x = pitch;
  return m;
}

// ---- The shared WORKS-SITE kit (M117f) ------------------------------------
//
// The owner's note on the M117d launchers: "the ramps still look too much like
// ramps and not enough like roadwork". The read to kill is a clean symmetrical
// wedge with a bright stripe up the middle. These helpers are the three moves
// that break it, shared by every city so the language stays one language:
//   1. BoxKit — dozens of site props for a handful of draw calls, so a
//      launcher can afford a real story (plant, fencing, spoil, stacks).
//   2. kickerSkirt — bedding fill heaped along the launcher's sides, which
//      buries the freestanding-wedge silhouette at its foot.
//   3. reflectorStuds — the go-fast cue as ROAD FURNITURE (a dotted line of
//      cat's eyes) instead of a glowing runway centre-line.

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);

/** Instanced-box batcher: bucket boxes by material, one draw call each. */
export class BoxKit {
  private readonly buckets = new Map<THREE.Material, THREE.Matrix4[]>();

  add(
    m: THREE.Material,
    x: number,
    y: number,
    z: number,
    yaw: number,
    w: number,
    h: number,
    d: number,
    pitch = 0,
  ): void {
    let list = this.buckets.get(m);
    if (!list) {
      list = [];
      this.buckets.set(m, list);
    }
    list.push(
      new THREE.Matrix4().compose(
        new THREE.Vector3(x, y, z),
        // §6.1 order: yaw first, then pitch about the yawed X (matches box()).
        new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, yaw, 0, 'YXZ')),
        new THREE.Vector3(w, h, d),
      ),
    );
  }

  /** Add a box in a PROP's local frame (+z nose, +x its right). */
  local(
    m: THREE.Material,
    base: { x: number; y: number; z: number },
    yaw: number,
    ox: number,
    oy: number,
    oz: number,
    w: number,
    h: number,
    d: number,
    pitch = 0,
  ): void {
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    this.add(m, base.x + Math.cos(yaw) * ox + fx * oz, base.y + oy, base.z - Math.sin(yaw) * ox + fz * oz, yaw, w, h, d, pitch);
  }

  flush(g: THREE.Group): void {
    for (const [m, mats] of this.buckets) {
      const inst = new THREE.InstancedMesh(UNIT_BOX, m, mats.length);
      for (let i = 0; i < mats.length; i++) inst.setMatrixAt(i, mats[i]);
      inst.instanceMatrix.needsUpdate = true;
      g.add(inst);
    }
    this.buckets.clear();
  }
}

/**
 * BEDDING FILL along both sides of a launcher (the silhouette breaker). A real
 * road plate is not a freestanding wedge — it is a sheet bedded into a heap of
 * compacted fill, and that heap is what makes it read as works rather than a
 * jump. The berm's inner edge follows the launcher's side up to `maxRise` then
 * stops climbing (so only the top of the slab shows a clean edge), its outer
 * edge is a jittered line lying on the ground, and it dies away toward the lip.
 */
export function kickerSkirt(
  track: Track,
  k: KickerFrame,
  material: THREE.Material,
  maxRise: number,
  outreach: number,
  seed: number,
  /** Which local side gets the DEEP heap (the verge the works spill from) —
   *  a symmetric berm re-reads as a designed wedge, so one flank is buried
   *  and the other is a thin lip (measured on the head-on approach). */
  heapSide: -1 | 1 = 1,
): THREE.Mesh {
  const fx = Math.sin(k.dir);
  const fz = Math.cos(k.dir);
  const rx = Math.cos(k.dir);
  const rz = -Math.sin(k.dir);
  const N = 13;
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  let base = 0;
  for (const s of [-1, 1] as const) {
    for (let i = 0; i <= N; i++) {
      const t = -0.05 + (i / N) * 1.02;
      const ax = k.x + fx * t * k.length;
      const az = k.z + fz * t * k.length;
      const iLat = s * (k.halfWidth + 0.02);
      const ix = ax + rx * iLat;
      const iz = az + rz * iLat;
      const iy = k.y + Math.min(Math.max(t, 0) * k.height, maxRise);
      const fade = Math.max(0.12, 1 - Math.max(0, t - 0.5) * 1.7);
      // MEASURED: at 0.68 the road-facing flank was effectively bare, and from
      // the racing line's 3/4 angle the slab went straight back to reading as
      // a clean steel wedge. Both sides need real fill; only the depth differs.
      const spread =
        outreach * (s === heapSide ? 1.5 : 1.0) * (0.5 + art(seed + i * 7 + (s > 0 ? 37 : 0)) * 1.0) * fade;
      const oLat = s * (k.halfWidth + 0.02 + spread);
      const ox = ax + rx * oLat;
      const oz = az + rz * oLat;
      const oy = track.heightAt(ox, oz) + 0.03;
      // MEASURED: pushing inner-then-outer on BOTH sides gives the two strips
      // opposite winding, so one berm's normals point DOWN and it renders as a
      // black wedge in sun — which looked exactly like the hard-edged ramp the
      // fill exists to hide. Mirror the push order with the side.
      if (s === 1) pos.push(ox, oy, oz, ix, iy, iz);
      else pos.push(ix, iy, iz, ox, oy, oz);
      uv.push(t * 4, 1, t * 4, 0);
      if (i > 0) {
        const kk = base + i * 2;
        idx.push(kk - 2, kk - 1, kk, kk - 1, kk + 1, kk);
      }
    }
    base += (N + 1) * 2;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  const mesh = new THREE.Mesh(g, material);
  mesh.matrixAutoUpdate = false;
  return mesh;
}

/**
 * THE BOOST CUE, as road furniture (owner round 3 asked for speed boosts on
 * the ramps; round 5 asked the ramps to stop reading as ramps). A dotted line
 * of recessed reflector studs down the slab: findable by anyone who knows to
 * look — and glinting at night — but incidental where a lit centre-line strip
 * was the single loudest "JUMP HERE" signal on the approach (measured: the
 * additive strip was the first thing the eye caught 45 u out).
 */
export function reflectorStuds(
  g: THREE.Group,
  k: KickerFrame,
  color: number,
  count = 6,
  glow = 0.75,
  bias = -0.85,
): void {
  const fx = Math.sin(k.dir);
  const fz = Math.cos(k.dir);
  const rx = Math.cos(k.dir);
  const rz = -Math.sin(k.dir);
  const pitch = -Math.atan2(k.height, k.length);
  const m = new THREE.MeshStandardMaterial({
    color: 0xb4dcea,
    roughness: 0.3,
    metalness: 0.25,
    emissive: new THREE.Color(color),
    emissiveIntensity: glow,
  });
  const inst = new THREE.InstancedMesh(UNIT_BOX, m, count);
  const mt = new THREE.Matrix4();
  const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, k.dir, 0, 'YXZ'));
  const sc = new THREE.Vector3(0.34, 0.08, 0.24);
  for (let i = 0; i < count; i++) {
    const t = 0.07 + (i / (count - 1)) * 0.85;
    // BIASED off the centre line: a dotted line up the MIDDLE is a runway
    // marking however small the dots are — an edge marker is road furniture.
    const lat = bias + (art(i * 17 + 5) - 0.5) * 0.7;
    inst.setMatrixAt(
      i,
      mt.compose(
        new THREE.Vector3(k.x + fx * t * k.length + rx * lat, k.y + t * k.height + 0.05, k.z + fz * t * k.length + rz * lat),
        q,
        sc,
      ),
    );
  }
  inst.instanceMatrix.needsUpdate = true;
  g.add(inst);
}

/** A run of site fence panels along the verge (one instanced draw call) with
 *  concrete feet batched into `kit`. Reads "closed site" from a long way out
 *  without the plywood wall's opacity. */
export function fenceRun(
  g: THREE.Group,
  kit: BoxKit,
  footMat: THREE.Material,
  at: (arc: number, lat: number) => { x: number; y: number; z: number; dir: number },
  fromArc: number,
  panels: number,
  lat: number,
  seed: number,
  panelLen = 2.7,
  panelH = 2.0,
): void {
  const mtl = new THREE.MeshStandardMaterial({
    map: fencePanelTexture(),
    roughness: 0.8,
    alphaTest: 0.42,
    side: THREE.DoubleSide,
  });
  const inst = new THREE.InstancedMesh(new THREE.PlaneGeometry(panelLen, panelH), mtl, panels);
  const mt = new THREE.Matrix4();
  for (let i = 0; i < panels; i++) {
    const arc = fromArc + i * (panelLen + 0.12);
    const jitter = (art(seed + i * 11) - 0.5) * 0.5;
    const p = at(arc, lat + jitter);
    const yaw = p.dir + Math.PI / 2 + (art(seed + i * 13) - 0.5) * 0.13;
    inst.setMatrixAt(
      i,
      mt.compose(
        new THREE.Vector3(p.x, p.y + panelH / 2 + 0.16, p.z),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0, 'YXZ')),
        new THREE.Vector3(1, 1, 1),
      ),
    );
    // Concrete foot under each joint.
    const f = at(arc - panelLen / 2, lat + jitter);
    kit.add(footMat, f.x, f.y + 0.1, f.z, f.dir, 0.34, 0.2, 1.0);
  }
  inst.instanceMatrix.needsUpdate = true;
  g.add(inst);
}

/** The chevron KICKER — one pitched wedge (top face IS the incline, the M99
 *  smooth-ramp lesson) + timber cheeks. Shared launcher language. */
export function buildKicker(g: THREE.Group, k: KickerFrame): void {
  const slopeLen = Math.hypot(k.length, k.height);
  const pitch = -Math.atan2(k.height, k.length);
  const deckMat = new THREE.MeshStandardMaterial({
    map: launcherTexture(),
    roughness: 0.85,
    emissive: new THREE.Color(0x59c8ff),
    emissiveIntensity: 0.18, // the boost strip whispers at night
  });
  const deck = new THREE.Mesh(new THREE.BoxGeometry(k.halfWidth * 2, 0.3, slopeLen), deckMat);
  deck.position.set(
    k.x + Math.sin(k.dir) * (k.length / 2),
    k.y + k.height / 2 - 0.05,
    k.z + Math.cos(k.dir) * (k.length / 2),
  );
  deck.rotation.order = 'YXZ';
  deck.rotation.y = k.dir;
  deck.rotation.x = pitch;
  g.add(deck);
  const cheekMat = mat(0x6a4a30, 0.9);
  for (const s of [-1, 1] as const) {
    const cx = k.x + Math.sin(k.dir) * (k.length * 0.55) + Math.cos(k.dir) * s * (k.halfWidth + 0.12);
    const cz = k.z + Math.cos(k.dir) * (k.length * 0.55) - Math.sin(k.dir) * s * (k.halfWidth + 0.12);
    g.add(box(0.2, k.height * 0.85, k.length * 0.9, cheekMat, cx, k.y + k.height * 0.4, cz, k.dir));
  }
}

/**
 * THE WORKS ZONE (M117d — the NYC launcher, owner: "improve the design of the
 * ramp in New York so that it looks like it's an obstacle construction zone
 * but operates like a ramp"). A steel road plate laid over an open trench,
 * dressed as live roadworks: the plate IS the kicker's incline (unchanged
 * physics), the trench mouth is a flat decal cars drive over harmlessly, and
 * every solid prop stands OUTSIDE the guardrail, so nothing here is a
 * collision the player can't see coming. The real lane-closure taper — the
 * cones and the barrier that funnel you onto the plate — are proper course
 * obstacles in cities.ts, so what looks like a hazard genuinely is one.
 */
function buildWorksKicker(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const k = sc.kicker as KickerFrame;
  const slopeLen = Math.hypot(k.length, k.height);
  const pitch = -Math.atan2(k.height, k.length);
  const cosP = Math.cos(pitch);
  const fx = Math.sin(k.dir);
  const fz = Math.cos(k.dir);
  const rx = Math.cos(k.dir);
  const rz = -Math.sin(k.dir);

  // ---- The plate: THREE SHEETS, none of them square to the run (M117f) ----
  // A single full-width slab is a wedge, and a wedge is a ramp no matter what
  // it is painted. Real plate-over-a-trench decking is sheets dragged into
  // place one at a time: they overlap, sit a centimetre proud of each other,
  // and none of them lines up. Every sheet's TOP FACE still lies in the one
  // launch plane (the spin is about the slope NORMAL, so the drivable surface
  // is unchanged) — only the outline is broken.
  const plateTex = steelPlateTexture();
  // One map, three tints: sheets on a real site are different ages, and equal
  // greys re-fuse the three back into one slab from a distance (measured).
  const plateMats = [0xffffff, 0xcfc7ba, 0xa89684].map(
    (tint) =>
      new THREE.MeshStandardMaterial({
        map: plateTex,
        color: tint,
        roughness: 0.78, // site-worn steel, not showroom chrome (a polished plate
        metalness: 0.3, // mirrored the dusk sky and read as a shiny feature)
      }),
  );
  const baseQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, k.dir, 0, 'YXZ'));
  const sheet = (mi: number, latOff: number, halfW: number, t0: number, t1: number, spin: number, lift: number): void => {
    const tm = (t0 + t1) / 2;
    const ax = k.x + fx * tm * k.length + rx * latOff;
    const az = k.z + fz * tm * k.length + rz * latOff;
    const m = new THREE.Mesh(new THREE.BoxGeometry(halfW * 2, 0.3, (t1 - t0) * slopeLen), plateMats[mi]);
    m.position.set(ax, k.y + tm * k.height - 0.15 * cosP + lift, az);
    // Post-multiplying a LOCAL-Y turn spins the sheet WITHIN the launch plane
    // (its top face normal is local +Y), so askew never means a lie.
    m.quaternion.copy(baseQ).multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), spin));
    g.add(m);
  };
  sheet(2, -0.62, 2.55, 0.0, 0.6, 0.07, 0);
  sheet(0, 0.78, 2.5, 0.03, 0.72, -0.075, 0.04);
  sheet(1, -0.05, 3.15, 0.57, 1.0, 0.028, 0.085);

  // The compacted fill the sheets are bedded on — without it the plate reads
  // as a sheet floating over the road (measured from the kerb view).
  const fillMat = mat(0x2f2c29, 0.98);
  const fill = new THREE.Mesh(new THREE.BoxGeometry(k.halfWidth * 2 - 0.24, 1.5, slopeLen - 0.3), fillMat);
  fill.position.set(k.x + fx * (k.length / 2), k.y + k.height / 2 - (0.3 + 0.75) * cosP, k.z + fz * (k.length / 2));
  fill.rotation.order = 'YXZ';
  fill.rotation.y = k.dir;
  fill.rotation.x = pitch;
  g.add(fill);
  // ...and the spoil heaped up against its flanks: the silhouette breaker.
  const bedMat = mat(0x413a31, 0.99);
  bedMat.side = THREE.DoubleSide;
  bedMat.flatShading = true;
  // maxRise 2.4 of the 3.4-u slab: below that the top TWO metres of bare steel
  // flank still read as a wedge from a 3/4 angle (measured from the road side).
  g.add(kickerSkirt(track, k, bedMat, 2.4, 2.6, 17, sc.door.side));

  // Broken-out lumps of the old carriageway tipped along the fill: they shade
  // correctly at any sun angle, which a thin ribbon of berm does not, so the
  // slab never gets a clean triangular edge back at dusk.
  const rubble = new BoxKit();
  for (let i = 0; i < 14; i++) {
    const t = art(i * 5) * 1.05 - 0.04;
    const s = i % 2 === 0 ? 1 : -1;
    const lat = s * (k.halfWidth + 0.35 + art(i * 9) * 2.1);
    const px = k.x + fx * t * k.length + rx * lat;
    const pz = k.z + fz * t * k.length + rz * lat;
    const h = 0.34 + art(i * 11) * 0.6;
    rubble.add(
      bedMat,
      px,
      track.heightAt(px, pz) + h * 0.42 + Math.min(Math.max(t, 0) * k.height, 1.9) * 0.5,
      pz,
      k.dir + art(i * 13) * 3.1,
      0.55 + art(i * 17) * 1.1,
      h,
      0.5 + art(i * 19) * 1.0,
      (art(i * 23) - 0.5) * 0.5,
    );
  }
  rubble.flush(g);

  // The go-fast cue: cat's eyes, not a centre-line (see reflectorStuds).
  reflectorStuds(g, k, 0x59c8ff, 7);

  // ---- Ground decals: the cut, and what plant tracks out of it -----------
  // Both are flat to the road = pure paint, nothing to hit (the M117d rule).
  const decal = (
    tex: THREE.CanvasTexture,
    w: number,
    d: number,
    ox: number,
    olat: number,
    yaw: number,
    alphaTest: boolean,
  ): void => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, d),
      new THREE.MeshStandardMaterial({
        map: tex,
        roughness: 0.98,
        ...(alphaTest ? { alphaTest: 0.45 } : { transparent: true, depthWrite: false }),
      }),
    );
    const px = k.x + fx * ox + rx * olat;
    const pz = k.z + fz * ox + rz * olat;
    m.position.set(px, track.heightAt(px, pz) + 0.035, pz);
    // 'YXZ': yaw to the launch heading FIRST, then lay it flat — the reverse
    // order skews a ground quad (measured).
    m.rotation.order = 'YXZ';
    m.rotation.y = yaw;
    m.rotation.x = -Math.PI / 2;
    g.add(m);
  };
  const trench = trenchTexture();
  decal(trench, k.halfWidth * 2 + 3.4, 4.6, -1.9, 0.4, k.dir, true);
  decal(trench, 5.2, 3.2, 5.5, 8.6, k.dir + 0.5, true); // a second cut on the verge
  decal(trackedDirtTexture(), 5.4, 11, -6.5, 1.6, k.dir + 0.06, false);
  // The steel lip where the sheets ride up out of the cut — deliberately NOT
  // full width and set askew, so it is a scrap of edging, not a launch bar.
  g.add(box(k.halfWidth * 1.35, 0.14, 0.4, mat(0x6a6e74, 0.5), k.x - fx * 0.2 + rx * 0.8, k.y + 0.07, k.z - fz * 0.2 + rz * 0.8, k.dir + 0.07));

  // ---- The site around it, ALL outside the guardrail ---------------------
  const side = sc.door.side;
  const kickArc = track.project(k.x, k.z, Math.round(sc.door.arc / track.step)).distance;
  const at = (arc: number, lat: number): { x: number; y: number; z: number; dir: number } => {
    const s = track.sampleAt(arc);
    const x = s.x + s.rightX * side * lat;
    const z = s.z + s.rightZ * side * lat;
    return { x, y: track.heightAt(x, z), z, dir: s.dir };
  };
  const kit = new BoxKit();
  const timber = mat(0x6a4a30, 0.92);
  const steel = mat(0x3a3d40, 0.72);
  const conc = mat(0x8d9095, 0.92);
  const plant = mat(0xd8a52a, 0.7); // works yellow
  const grime = mat(0x2a2b2f, 0.95);

  // A long run of site fencing UP-COURSE of the plate: the closed lane has to
  // be established before you arrive, or the plate is the only thing you see.
  fenceRun(g, kit, conc, at, kickArc - 34, 7, 19.2, 5);

  // Plywood hoarding beside the dig itself (opaque where the work is) — kept
  // UP-COURSE of the plate only: a panel past it stood exactly on the sight
  // line to the excavator and reduced the machine to a yellow sliver.
  const hoardMat = new THREE.MeshStandardMaterial({
    map: hoardingTexture(),
    roughness: 0.92,
    side: THREE.DoubleSide,
  });
  for (const [arc, len] of [
    [kickArc - 7, 9],
    [kickArc - 17, 5],
  ] as const) {
    const h = at(arc, 19.4);
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(len, 2.3), hoardMat);
    panel.position.set(h.x, h.y + 1.15, h.z);
    panel.rotation.y = h.dir + Math.PI / 2;
    g.add(panel);
    for (const t of [-0.5, 0.5]) {
      const p = at(arc + len * t * 0.94, 19.7);
      kit.add(timber, p.x, p.y + 1.2, p.z, p.dir, 0.16, 2.4, 0.16);
    }
  }

  // Amber work lamp on a post — the one warm light in the zone.
  const lampPost = at(kickArc - 1, 18.9);
  kit.add(steel, lampPost.x, lampPost.y + 1.1, lampPost.z, lampPost.dir, 0.14, 2.2, 0.14);
  const lamp = new THREE.Mesh(
    new THREE.SphereGeometry(0.24, 8, 6),
    new THREE.MeshStandardMaterial({
      color: 0xffb347,
      emissive: new THREE.Color(0xffa02a),
      emissiveIntensity: 1.6,
      roughness: 0.4,
    }),
  );
  lamp.position.set(lampPost.x, lampPost.y + 2.25, lampPost.z);
  g.add(lamp);

  // THE MINI EXCAVATOR — one prop does more for "works site" than any amount
  // of paint. Parked nose-OUT on the verge so its arm never reaches over the
  // road (a bucket hanging into the lane would read as a hittable obstacle),
  // and set in the GAP past the hoarding: behind a 2.3-u panel it was a
  // yellow sliver, and the machine is the whole point (measured).
  const dig = at(kickArc - 25, 22);
  const digYaw = dig.dir + 0.62;
  kit.local(grime, dig, digYaw, -0.9, 0.32, 0, 0.58, 0.64, 3.4); // tracks
  kit.local(grime, dig, digYaw, 0.9, 0.32, 0, 0.58, 0.64, 3.4);
  kit.local(grime, dig, digYaw, 0, 0.78, 0, 1.9, 0.3, 2.6); // slew ring
  kit.local(plant, dig, digYaw, 0, 1.52, -0.5, 2.0, 1.25, 2.0); // house
  kit.local(grime, dig, digYaw, 0, 1.3, -1.55, 1.95, 0.75, 0.55); // counterweight
  kit.local(steel, dig, digYaw, -0.44, 1.62, 0.62, 1.14, 1.34, 1.14); // cab
  kit.local(plant, dig, digYaw, 0.32, 2.86, 1.98, 0.46, 0.5, 3.2, -0.55); // boom
  kit.local(plant, dig, digYaw, 0.32, 2.5, 3.86, 0.4, 0.4, 2.6, 1.15); // dipper
  kit.local(grime, dig, digYaw, 0.32, 0.62, 4.5, 1.0, 0.95, 0.85, 0.35); // bucket

  // A skip full of broken-out asphalt, and a site cabin behind it.
  const skip = at(kickArc + 17, 21.6);
  kit.add(plant, skip.x, skip.y + 0.72, skip.z, skip.dir + 0.24, 2.5, 1.45, 4.0);
  kit.add(grime, skip.x, skip.y + 1.55, skip.z, skip.dir + 0.18, 2.1, 0.6, 3.4);
  const hut = at(kickArc - 12, 24.6);
  const hutYaw = hut.dir - 0.2;
  kit.local(conc, hut, hutYaw, 0, 0.14, 0, 3.4, 0.28, 2.6); // blocks it stands on
  kit.local(mat(0xdadfe2, 0.85), hut, hutYaw, 0, 1.42, 0, 3.1, 2.3, 2.3); // cabin
  kit.local(grime, hut, hutYaw, -1.5, 1.3, 0.2, 0.12, 1.9, 0.9); // door, facing the road

  // A STACK OF SPARE PLATES on the verge — the prop that explains the ramp:
  // once you have seen sheets waiting in a pile, the one over the trench
  // reads as site kit rather than a launcher.
  for (let i = 0; i < 3; i++) {
    const p = at(kickArc - 3.5 + i * 0.5, 20.8 + i * 0.25);
    const stack = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.26, 4.2), plateMats[i % 3]);
    stack.position.set(p.x, p.y + 0.14 + i * 0.24, p.z);
    stack.rotation.order = 'YXZ';
    stack.rotation.y = p.dir + 0.25 + (art(i * 13) - 0.5) * 0.36;
    stack.rotation.x = 0.03 * (art(i * 7) - 0.5);
    g.add(stack);
  }

  // Stacked kerbstones + a heap of pipe, the way a dig spills onto the verge.
  for (let i = 0; i < 7; i++) {
    const p = at(kickArc - 20 + (i % 4) * 1.15, 21.4 + Math.floor(i / 4) * 0.9);
    kit.add(conc, p.x, p.y + 0.14 + Math.floor(i / 4) * 0.3, p.z, p.dir + (art(i * 7) - 0.5) * 0.3, 0.4, 0.3, 1.0);
  }
  const heap = at(kickArc + 11.5, 19.6);
  const spoil = new THREE.Mesh(new THREE.ConeGeometry(2.4, 1.5, 7), mat(0x3b3630, 0.98));
  spoil.position.set(heap.x, heap.y + 0.66, heap.z);
  spoil.rotation.y = art(3) * Math.PI;
  g.add(spoil);
  const pipeMat = mat(0x5c6166, 0.8);
  const pipeGeo = new THREE.CylinderGeometry(0.34, 0.34, 4.4, 8);
  for (const [i, lat] of [19.7, 20.4, 20.05].entries()) {
    const p = at(kickArc - 16 + i * 0.4, lat);
    const pipe = new THREE.Mesh(pipeGeo, pipeMat);
    pipe.position.set(p.x, p.y + 0.34 + (i === 2 ? 0.6 : 0), p.z);
    pipe.rotation.order = 'YXZ';
    pipe.rotation.y = p.dir + 0.08;
    pipe.rotation.x = Math.PI / 2;
    g.add(pipe);
  }
  // Sandbags weighting the fence line + a row of them along the cut's edge.
  const bagMat = mat(0x8a7f63, 0.98);
  for (let i = 0; i < 8; i++) {
    const p = at(kickArc - 9 + i * 1.5, 18.75 + art(i * 5) * 0.4);
    kit.add(bagMat, p.x, p.y + 0.14, p.z, p.dir + (art(i * 9) - 0.5) * 0.6, 0.72, 0.28, 0.44);
  }
  // Concrete barriers closing the kerb lane alongside the dig.
  for (let i = 0; i < 5; i++) {
    const p = at(kickArc - 2 + i * 2.5, 18.5 + art(i * 11) * 0.3);
    kit.add(conc, p.x, p.y + 0.42, p.z, p.dir + (art(i * 3) - 0.5) * 0.1, 0.62, 0.84, 2.3);
  }
  kit.flush(g);
}

/** Full covered gallery: two walls + ceiling + floor (cavern only). */
function boreShell(
  g: THREE.Group,
  sc: PlacedShortcut,
  from: number,
  to: number,
  p: Palette,
  wallTex: THREE.CanvasTexture,
  floorMat: THREE.Material,
  ceilOverride?: THREE.Material,
): void {
  const ch = sc.channel;
  const half = ch.halfWidth + 0.5;
  const H = INTERIOR_H();
  const wallMat = new THREE.MeshStandardMaterial({
    map: wallTex,
    roughness: 0.85,
    emissive: new THREE.Color(0xffffff),
    emissiveMap: wallTex,
    emissiveIntensity: 0.14,
    side: THREE.DoubleSide,
  });
  const ceilMat = ceilOverride ?? mat(p.trim, 0.95);
  ceilMat.side = THREE.DoubleSide;
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, from, to, (nd) => {
        const x = nd.x + nd.rightX * s * half;
        const z = nd.z + nd.rightZ * s * half;
        return [new THREE.Vector3(x, nd.y - 0.3, z), new THREE.Vector3(x, nd.y + H, z)];
      }, wallMat),
    );
  }
  g.add(
    chStrip(ch.nodes, ch.step, from, to, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * half, nd.y + H, nd.z - nd.rightZ * half),
      new THREE.Vector3(nd.x + nd.rightX * half, nd.y + H, nd.z + nd.rightZ * half),
    ], ceilMat),
  );
  g.add(
    chStrip(ch.nodes, ch.step, from, to, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * half, nd.y + 0.02, nd.z - nd.rightZ * half),
      new THREE.Vector3(nd.x + nd.rightX * half, nd.y + 0.02, nd.z + nd.rightZ * half),
    ], floorMat),
  );
  glowStrips(g, ch, from, to, half - 0.08, 3.4, p.accent);
}

/**
 * The OPEN ELEVATED DECK: floor, side rails with glow, a dark underside skirt,
 * support columns to the ground, and a slightly flared leading-edge apron (the
 * landing invitation).
 *
 * CURRENTLY UNREFERENCED (shared-pass). Both callers — highline and skypath —
 * now build their own decks, because this generic version is exactly what made
 * them read as flat ribbons on sticks: one thin untextured skirt for the whole
 * underside and no material anywhere. It is kept, not deleted, for two
 * reasons: it is the canonical statement of the CANTILEVER CONTRACT that
 * shortcutTokyo.ts and both new builders cite by name (never a column where
 * the spine sits within road halfWidth + 1.6 — the deck cantilevers over the
 * road instead), and sibling shortcut builders are in flight in this tree
 * while this is being written. Prune it once the batch settles and nothing
 * has picked it up.
 */
export function deckStructure(
  g: THREE.Group,
  track: Track,
  sc: PlacedShortcut,
  floorMat: THREE.Material,
  railMat: THREE.Material,
  colMat: THREE.Material,
  accent: number,
  colEvery: number,
): void {
  const ch = sc.channel;
  const half = ch.halfWidth + 0.35;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  // Floor.
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * half, nd.y + 0.03, nd.z - nd.rightZ * half),
      new THREE.Vector3(nd.x + nd.rightX * half, nd.y + 0.03, nd.z + nd.rightZ * half),
    ], floorMat, 5),
  );
  // Underside skirt (the deck must read as a slab from the road below).
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * half, nd.y - 0.55, nd.z - nd.rightZ * half),
      new THREE.Vector3(nd.x + nd.rightX * half, nd.y - 0.55, nd.z + nd.rightZ * half),
    ], colMat, 5),
  );
  // Side rails: solid low band + thin top rail.
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * half;
        const z = nd.z + nd.rightZ * s * half;
        return [new THREE.Vector3(x, nd.y - 0.55, z), new THREE.Vector3(x, nd.y + RAIL_H, z)];
      }, railMat, 4),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * (half + 0.06);
        const z = nd.z + nd.rightZ * s * (half + 0.06);
        return [new THREE.Vector3(x, nd.y + RAIL_H, z), new THREE.Vector3(x, nd.y + RAIL_H + 0.12, z)];
      }, colMat, 4),
    );
  }
  glowStrips(g, ch, 0, nEnd, half - 0.05, RAIL_H - 0.18, accent);
  // Support columns wherever the spine is clear of the road (a column ON the
  // racing line would be a physics lie — the deck cantilevers over the road).
  for (let i = 1; i < nEnd; i += colEvery) {
    const nd = ch.nodes[i];
    const proj = track.project(nd.x, nd.z, Math.round(sc.entryArc / track.step));
    if (Math.abs(proj.lateral) < track.halfWidth + 1.6) continue;
    const groundY = track.heightAt(nd.x, nd.z);
    const h = nd.y - 0.4 - groundY;
    if (h < 1.2) continue;
    g.add(box(0.55, h, 0.55, colMat, nd.x, groundY + h / 2, nd.z, nd.dir));
  }
  // Leading-edge apron: a wider catch lip + end plate, so the deck start
  // reads from the ramp ("that's where I land").
  const lipMat = colMat;
  const n0 = ch.nodes[0];
  g.add(
    chStrip(ch.nodes, ch.step, 0, 2, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * (half + 0.7), nd.y + 0.05, nd.z - nd.rightZ * (half + 0.7)),
      new THREE.Vector3(nd.x + nd.rightX * (half + 0.7), nd.y + 0.05, nd.z + nd.rightZ * (half + 0.7)),
    ], floorMat, 5),
  );
  g.add(box((half + 0.7) * 2, 0.7, 0.25, lipMat, n0.x, n0.y - 0.35, n0.z, n0.dir));
  // A faint glow under the lip — barely-there (the landing hides at the
  // course edge now; discovery is the flight, not a beacon).
  const glow = new THREE.Mesh(
    new THREE.PlaneGeometry((half + 0.6) * 2, 1.4),
    new THREE.MeshBasicMaterial({
      color: accent,
      transparent: true,
      opacity: 0.15,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  glow.position.set(n0.x, n0.y - 0.9, n0.z);
  glow.rotation.y = n0.dir;
  g.add(glow);
}

/** Exit dressing shared by the open decks: jamb posts + a small berm where
 *  the chute meets the road-grade opening. */
export function dressExit(g: THREE.Group, sc: PlacedShortcut, m: THREE.Material): void {
  const d = sc.exitDoor;
  for (const s of [-1, 1] as const) {
    g.add(
      box(0.4, 1.6, 0.4, m, d.x + d.dirX * s * (d.half + 0.6), d.y + 0.8, d.z + d.dirZ * s * (d.half + 0.6), Math.atan2(d.dirX, d.dirZ)),
    );
  }
}

// ---- Rio: the Tijuca cavern ----------------------------------------------

function buildCavern(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const p = PALETTES.cavern;
  const ch = sc.channel;
  const cfg = tuning.shortcuts;
  const rockTex = rockTexture();
  const rockMat = new THREE.MeshStandardMaterial({
    map: rockTex,
    roughness: 0.96,
    emissive: new THREE.Color(0xffffff),
    emissiveMap: rockTex,
    emissiveIntensity: 0.38,
    side: THREE.DoubleSide,
  });
  const sandMat = mat(p.floor, 0.95);
  sandMat.side = THREE.DoubleSide;

  if (sc.kicker) buildKicker(g, sc.kicker);

  // The cliff face with a real gap at the mouth window.
  const lift = sc.mouthY !== undefined ? sc.mouthY - sc.door.y : cfg.mouthLift;
  const cliffTop = lift + 9;
  const mHalf = sc.door.half + 1.2;
  const mTop = lift + 5.2;
  const jag = (t: number, amp: number, seed: number): number => (art(Math.floor(t * 14) + seed) - 0.5) * amp;
  const bands: [number, number, (t: number, gr: number) => number, (t: number, gr: number) => number][] = [
    [sc.door.arc - 22, sc.door.arc - mHalf + 1.5, (_t, gr) => gr - 0.6, (t, gr) => gr + cliffTop + jag(t, 2.4, 5)],
    [sc.door.arc + mHalf - 1.5, sc.door.arc + 18, (_t, gr) => gr - 0.6, (t, gr) => gr + cliffTop + jag(t, 2.4, 9)],
    [sc.door.arc - mHalf - 1, sc.door.arc + mHalf + 1, (_t, gr) => gr - 0.6, () => sc.door.y + lift - 1.2],
    [sc.door.arc - mHalf - 1, sc.door.arc + mHalf + 1, () => sc.door.y + mTop - 0.4, (t, gr) => gr + cliffTop + jag(t, 2.4, 13)],
  ];
  for (const [a, b, yb, yt] of bands) {
    g.add(wallBand(track, sc.door.side, a, b, 0.1, yb, yt, rockMat, 5));
  }
  // The hillside massing behind + shoulder wedges tying into the verge.
  g.add(
    wallBand(track, sc.door.side, sc.door.arc - 26, sc.door.arc + 22, 5.2, (_t, gr) => gr - 0.8, (t, gr) => gr + cliffTop + 4.5 + jag(t, 3.2, 21), rockMat, 7),
  );
  for (const [a0, a1, s0, s1] of [
    [sc.door.arc - 30, sc.door.arc - 22, 0.15, 1],
    [sc.door.arc + 22, sc.door.arc + 28, 1, 0.1],
  ] as const) {
    g.add(
      wallBand(track, sc.door.side, a0, a1, 2.4, (_t, gr) => gr - 0.6, (t, gr) => gr + (s0 + (s1 - s0) * t) * cliffTop * 0.85, rockMat, 3),
    );
  }
  const mouthMat = new THREE.MeshStandardMaterial({
    map: mouthFaceTexture(),
    alphaTest: 0.5,
    roughness: 0.96,
    side: THREE.DoubleSide,
  });
  const ms = track.sampleAt(sc.door.arc);
  const mouth = new THREE.Mesh(new THREE.PlaneGeometry(mHalf * 2, mTop - lift + 2.4), mouthMat);
  mouth.position.set(
    ms.x + ms.rightX * sc.door.side * (track.halfWidth + 0.05),
    sc.door.y + (lift - 1.2 + mTop) / 2,
    ms.z + ms.rightZ * sc.door.side * (track.halfWidth + 0.05),
  );
  mouth.rotation.y = ms.dir + (sc.door.side > 0 ? Math.PI : 0);
  g.add(mouth);

  // Gallery + open sand chute.
  const chuteLen = 24;
  const chuteStart = Math.max(10, ch.length - cfg.stubLen - chuteLen);
  const nChute = nodeAt(ch, chuteStart);
  const nExit = nodeAt(ch, ch.length - cfg.stubLen);
  boreShell(g, sc, 0, nChute, p, rockTex, sandMat, rockMat);
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, nChute, nExit, (nd, i) => {
        const x = nd.x + nd.rightX * s * (ch.halfWidth + 0.4);
        const z = nd.z + nd.rightZ * s * (ch.halfWidth + 0.4);
        return [new THREE.Vector3(x, nd.y - 0.4, z), new THREE.Vector3(x, nd.y + 2.2 + art(i + (s > 0 ? 40 : 80)) * 1.4, z)];
      }, rockMat),
    );
  }
  g.add(
    chStrip(ch.nodes, ch.step, nChute, nExit, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * (ch.halfWidth + 0.4), nd.y + 0.02, nd.z - nd.rightZ * (ch.halfWidth + 0.4)),
      new THREE.Vector3(nd.x + nd.rightX * (ch.halfWidth + 0.4), nd.y + 0.02, nd.z + nd.rightZ * (ch.halfWidth + 0.4)),
    ], sandMat, 4),
  );
  glowStrips(g, ch, 0, nodeAt(ch, 14), ch.halfWidth - 0.1, 4.2, p.accent);
  dressExit(g, sc, rockMat);
}

// ---- NYC: the park High Line ---------------------------------------------
//
// shared-pass: this was `deckStructure` with a concrete map and nothing else,
// and from the road below it read as a flat black ribbon on plain sticks. Two
// reasons, both fixed here:
//
//  1. NO STRUCTURE. The generic deck's whole underside was one 0.55-u skirt in
//     a flat dark colour, so the thing had no depth and no material. It wears
//     Tokyo's viaduct treatment now — a real girder side profile with a
//     textured fascia and soffit sharing ONE bottom line sampled at the spine
//     and clamped to the terrain, so the girder beds into a crest instead of
//     digging through it and opens to full depth where the deck flies.
//  2. NO PLANTING. This is the PARK High Line and it had no plants, because
//     shortcutClear.CORRIDOR_CLEAR sweeps every zone-placed tree and shrub out
//     of a 14-u right-of-way along the path — correctly, or the deck would fly
//     through Central Park's canopy. Anything green here therefore has to be
//     part of the deck itself, which is what the planter beds below are.
//
// The beds sit OUTBOARD of the running surface, on the structural overhang:
// channel.halfWidth (3.1) is the physics clamp and is NOT touched, the concrete
// runs to 3.45 as it always did, and the planting occupies 3.45 -> 4.7 behind a
// kerb. A car pinned at the clamp is a third of a metre off the kerb, so the
// planting reads as the thing keeping you on the deck without ever being it.

const HL_GIRDER_D = 1.5; // girder depth below the deck floor
const HL_KERB = 0.35; // running surface half-width, past the physics clamp
const HL_BED = 1.6; // deck half-width including the planter beds

function buildHighline(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const p = PALETTES.highline;
  if (sc.kicker) buildWorksKicker(track, sc, g);
  const ch = sc.channel;
  const halfDrive = ch.halfWidth + HL_KERB;
  const halfDeck = ch.halfWidth + HL_BED;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);

  const floorMat = new THREE.MeshStandardMaterial({
    map: concreteDeckTexture(),
    roughness: 0.9,
    side: THREE.DoubleSide,
  });
  const girderMat = new THREE.MeshStandardMaterial({
    map: deckStructTexture('#7d817f', 14, 46),
    roughness: 0.93,
    side: THREE.DoubleSide,
  });
  const soffitMat = new THREE.MeshStandardMaterial({
    map: deckStructTexture('#5e625f', 18, 34),
    roughness: 0.95,
    side: THREE.DoubleSide,
  });
  const steelMat = mat(0x2e3336, 0.55);
  steelMat.side = THREE.DoubleSide;
  const kerbMat = mat(0x9aa0a4, 0.85);
  kerbMat.side = THREE.DoubleSide;
  // Bed surface: bark-and-gravel mulch, NOT wet earth. At 0x3a2f22 the beds
  // read from the deck as a bare brown trench either side of the running
  // surface (measured); a lighter, greyer mulch sits under the planting
  // instead of competing with it.
  const soilMat = mat(0x4c4438, 0.98);
  soilMat.side = THREE.DoubleSide;

  // The running surface.
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * halfDrive, nd.y + 0.03, nd.z - nd.rightZ * halfDrive),
      new THREE.Vector3(nd.x + nd.rightX * halfDrive, nd.y + 0.03, nd.z + nd.rightZ * halfDrive),
    ], floorMat, 5),
  );

  // Planter beds + their kerbs, outboard of the running surface.
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * halfDrive;
        const z = nd.z + nd.rightZ * s * halfDrive;
        return [new THREE.Vector3(x, nd.y - 0.1, z), new THREE.Vector3(x, nd.y + 0.34, z)];
      }, kerbMat, 6),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
        new THREE.Vector3(nd.x + nd.rightX * s * halfDrive, nd.y + 0.3, nd.z + nd.rightZ * s * halfDrive),
        new THREE.Vector3(nd.x + nd.rightX * s * halfDeck, nd.y + 0.26, nd.z + nd.rightZ * s * halfDeck),
      ], soilMat, 4),
    );
  }

  // BOX GIRDER. Fascia AND soffit share one bottom line sampled at the SPINE
  // (a per-edge ground sample leaves a seam between their bottoms wherever the
  // deck runs close over a crest — measured on the Tokyo spur), clamped so the
  // girder beds into the ground rather than boring through it.
  const girderBot = (nd: { x: number; y: number; z: number }): number =>
    Math.max(track.heightAt(nd.x, nd.z) - 0.35, nd.y - HL_GIRDER_D);
  for (const s of [-1, 1] as const) {
    // Through-girder web: stands PROUD of the deck (the High Line's real
    // silhouette is a riveted beam edge you read from the street below).
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * halfDeck;
        const z = nd.z + nd.rightZ * s * halfDeck;
        return [new THREE.Vector3(x, girderBot(nd), z), new THREE.Vector3(x, nd.y + 0.5, z)];
      }, girderMat, 6),
    );
    // Bottom flange: the lip that turns a flat band into a beam in silhouette.
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
        new THREE.Vector3(nd.x + nd.rightX * s * halfDeck, girderBot(nd) + 0.02, nd.z + nd.rightZ * s * halfDeck),
        new THREE.Vector3(
          nd.x + nd.rightX * s * (halfDeck + 0.22),
          girderBot(nd) + 0.06,
          nd.z + nd.rightZ * s * (halfDeck + 0.22),
        ),
      ], steelMat, 5),
    );
  }
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * halfDeck, girderBot(nd), nd.z - nd.rightZ * halfDeck),
      new THREE.Vector3(nd.x + nd.rightX * halfDeck, girderBot(nd), nd.z + nd.rightZ * halfDeck),
    ], soffitMat, 6),
  );

  // Handrail over the girder web + its posts (one instanced draw).
  const kit = new BoxKit();
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * (halfDeck + 0.04);
        const z = nd.z + nd.rightZ * s * (halfDeck + 0.04);
        return [new THREE.Vector3(x, nd.y + 0.98, z), new THREE.Vector3(x, nd.y + 1.1, z)];
      }, steelMat, 4),
    );
    for (let i = 2; i < nEnd; i += 4) {
      const nd = ch.nodes[i];
      kit.add(
        steelMat,
        nd.x + nd.rightX * s * (halfDeck + 0.04),
        nd.y + 0.76,
        nd.z + nd.rightZ * s * (halfDeck + 0.04),
        nd.dir,
        0.11,
        0.56,
        0.11,
      );
    }
  }
  glowStrips(g, ch, 0, nEnd, halfDeck - 0.04, 0.62, p.accent);

  // PLANTING: crossed billboards in the beds. Both blades of a tuft go into
  // ONE instanced mesh, so the whole run of prairie grass is a single draw.
  const plantMat = new THREE.MeshStandardMaterial({
    map: plantingTexture(),
    alphaTest: 0.45,
    roughness: 0.92,
    side: THREE.DoubleSide,
  });
  const tufts: THREE.Matrix4[] = [];
  const tuftV = new THREE.Vector3();
  const tuftQ = new THREE.Quaternion();
  const tuftS = new THREE.Vector3();
  for (let i = 1; i < nEnd; i++) {
    const nd = ch.nodes[i];
    for (const s of [-1, 1] as const) {
      const seed = i * 31 + (s > 0 ? 17 : 0);
      // Gaps keep it from reading as a ruled hedge, but at 0.42 the beds were
      // visibly bare between clumps from deck height (measured) — 0.22 fills
      // them while still breaking the line.
      if (art(seed) < 0.22) continue;
      const lat = s * (ch.halfWidth + HL_KERB + 0.25 + art(seed * 3) * (HL_BED - HL_KERB - 0.45));
      const h = 0.85 + art(seed * 5) * 0.95;
      const w = 1.1 + art(seed * 7) * 0.7;
      for (const yaw of [0, Math.PI / 2]) {
        tufts.push(
          new THREE.Matrix4().compose(
            tuftV.set(nd.x + nd.rightX * lat, nd.y + 0.28 + h / 2, nd.z + nd.rightZ * lat),
            tuftQ.setFromEuler(new THREE.Euler(0, nd.dir + yaw + (art(seed * 11) - 0.5) * 0.5, 0, 'YXZ')),
            tuftS.set(w, h, 1),
          ),
        );
      }
    }
  }
  if (tufts.length) {
    const inst = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), plantMat, tufts.length);
    for (let i = 0; i < tufts.length; i++) inst.setMatrixAt(i, tufts[i]);
    inst.instanceMatrix.needsUpdate = true;
    g.add(inst);
  }

  // Steel columns with a cross-head, on the SAME cantilever contract the whole
  // series uses: never a column on or beside the racing line, the deck
  // cantilevers over the road. Projection is HINT-LESS (M99) — the spine bows
  // far outboard over the park and a hinted window clamps and misreads lateral
  // exactly where the skip matters.
  for (let i = 3; i < nEnd - 2; i += 4) {
    const nd = ch.nodes[i];
    const proj = track.project(nd.x, nd.z);
    if (Math.abs(proj.lateral) < track.halfWidth + 1.6) continue;
    const botY = nd.y - HL_GIRDER_D;
    const colH = botY - proj.y;
    if (colH < 1) continue;
    kit.add(steelMat, nd.x, proj.y - 0.2 + (colH + 0.2) / 2, nd.z, nd.dir, 0.62, colH + 0.2, 0.62);
    if (colH >= 2.2) {
      kit.add(steelMat, nd.x, botY - 0.3, nd.z, nd.dir, 3.4, 0.34, 0.5); // cross-head
      kit.add(steelMat, nd.x, proj.y + 0.12, nd.z, nd.dir, 1.5, 0.24, 1.5); // base plate
    }
  }

  // Landing apron: wider catch lip + end plate + the faint under-glow, the
  // series' landing-invitation language in the park's warm lamp colour.
  const n0 = ch.nodes[0];
  g.add(
    chStrip(ch.nodes, ch.step, 0, 2, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * (halfDeck + 0.5), nd.y + 0.05, nd.z - nd.rightZ * (halfDeck + 0.5)),
      new THREE.Vector3(nd.x + nd.rightX * (halfDeck + 0.5), nd.y + 0.05, nd.z + nd.rightZ * (halfDeck + 0.5)),
    ], floorMat, 5),
  );
  g.add(box((halfDeck + 0.5) * 2, 0.7, 0.25, steelMat, n0.x, n0.y - 0.35, n0.z, n0.dir));
  const glow = new THREE.Mesh(
    new THREE.PlaneGeometry((halfDeck + 0.1) * 2, 1),
    new THREE.MeshBasicMaterial({
      color: p.accent,
      transparent: true,
      opacity: 0.12,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  glow.position.set(n0.x, n0.y - 0.8, n0.z);
  glow.rotation.y = n0.dir;
  g.add(glow);

  kit.flush(g);
  dressExit(g, sc, steelMat);
}

// ---- Lagos: the lagoon sky-path ------------------------------------------
//
// shared-pass: same story as the High Line — the generic deck put a flat brown
// band under a flat brown rail and the sky-path read as masonry, which is the
// one thing a pier is not. It matches the JETTY now (the ramp that launches
// you onto it, and the project's stated bar for deck work): the same plank map
// under the same weathered wash, stringers and a bull-rail down the edges,
// timber piles braced in pairs, mooring posts at quay rhythm, and a sloped
// board apron capping the chute end so the deck stops meeting the road on a
// knife edge. The jetty RAMP itself is untouched.

const SP_SKIRT_D = 1.25; // plank fascia depth below the deck boards

function buildSkypath(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const p = PALETTES.skypath;
  const ch = sc.channel;
  const half = ch.halfWidth + 0.35;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  const nStub = nodeAt(ch, ch.length - tuning.shortcuts.stubLen);

  // The jetty's exact recipe: plank map under a grey-brown wash, because fresh
  // orange boards read as a built ramp and a weathered deck reads as decking
  // that has been out in the lagoon for years.
  const deckPlanks = plankTexture();
  const deckMat = new THREE.MeshStandardMaterial({
    map: deckPlanks,
    color: 0xa4907a,
    roughness: 0.94,
    side: THREE.DoubleSide,
  });
  const skirtPlanks = plankTexture();
  skirtPlanks.repeat.set(1, 0.5); // the fascia shows ~4 board courses, not 8
  const skirtMat = new THREE.MeshStandardMaterial({
    map: skirtPlanks,
    color: 0x9a8468,
    roughness: 0.95,
    side: THREE.DoubleSide,
  });
  const beamMat = mat(p.trim, 0.9);
  beamMat.side = THREE.DoubleSide;
  const pileMat = mat(0x46311d, 0.95);
  pileMat.side = THREE.DoubleSide;

  // Deck boards.
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * half, nd.y + 0.03, nd.z - nd.rightZ * half),
      new THREE.Vector3(nd.x + nd.rightX * half, nd.y + 0.03, nd.z + nd.rightZ * half),
    ], deckMat, 5),
  );

  const kit = new BoxKit();
  for (const s of [-1, 1] as const) {
    // Planked side skirt + the stringer beam capping it.
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * half;
        const z = nd.z + nd.rightZ * s * half;
        return [new THREE.Vector3(x, nd.y - SP_SKIRT_D, z), new THREE.Vector3(x, nd.y - 0.02, z)];
      }, skirtMat, 6),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * (half + 0.1);
        const z = nd.z + nd.rightZ * s * (half + 0.1);
        return [new THREE.Vector3(x, nd.y - 0.5, z), new THREE.Vector3(x, nd.y - 0.16, z)];
      }, beamMat, 5),
    );
    // BULL-RAIL: the timber kerb every working pier has, and the profile that
    // stops the deck edge reading as a moulded lip.
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * (half + 0.14);
        const z = nd.z + nd.rightZ * s * (half + 0.14);
        return [new THREE.Vector3(x, nd.y + 0.02, z), new THREE.Vector3(x, nd.y + 0.36, z)];
      }, beamMat, 5),
    );
    // Timber handrail on posts above it.
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * (half + 0.14);
        const z = nd.z + nd.rightZ * s * (half + 0.14);
        return [new THREE.Vector3(x, nd.y + 0.94, z), new THREE.Vector3(x, nd.y + 1.1, z)];
      }, beamMat, 5),
    );
    for (let i = 2; i < nEnd; i += 3) {
      const nd = ch.nodes[i];
      const x = nd.x + nd.rightX * s * (half + 0.14);
      const z = nd.z + nd.rightZ * s * (half + 0.14);
      // MOORING POSTS at quay rhythm (every 4th bay a taller capped post) —
      // the detail that says "pier" rather than "walkway".
      const moor = i % 12 === 2;
      kit.add(pileMat, x, nd.y + (moor ? 0.62 : 0.5), z, nd.dir, 0.16, moor ? 1.44 : 1.1, 0.16);
      if (moor) kit.add(beamMat, x, nd.y + 1.4, z, nd.dir, 0.3, 0.14, 0.3);
    }
  }
  glowStrips(g, ch, 0, nEnd, half + 0.02, 0.52, p.accent); // the lantern line

  // PILES + CROSS-BRACING, in bents. Hint-less projection and the series'
  // cantilever contract: no leg on or beside the racing line.
  let prevBent: { x: number; z: number; top: number; ground: number } | null = null;
  for (let i = 2; i < nEnd - 1; i += 3) {
    const nd = ch.nodes[i];
    const proj = track.project(nd.x, nd.z);
    if (Math.abs(proj.lateral) < track.halfWidth + 1.6) {
      prevBent = null;
      continue;
    }
    const top = nd.y - SP_SKIRT_D;
    const h = top - proj.y;
    if (h < 1.2) {
      prevBent = null;
      continue;
    }
    for (const s of [-1, 1] as const) {
      const lat = s * (half - 0.35);
      kit.add(
        pileMat,
        nd.x + nd.rightX * lat,
        proj.y + h / 2,
        nd.z + nd.rightZ * lat,
        nd.dir,
        0.34,
        h + 0.3,
        0.34,
      );
    }
    // Waling beam tying the bent's legs together, low down.
    kit.add(pileMat, nd.x, proj.y + h * 0.32, nd.z, nd.dir, half * 2, 0.2, 0.22);
    // X-brace between this bent and the last one.
    if (prevBent) {
      const dx = nd.x - prevBent.x;
      const dz = nd.z - prevBent.z;
      const span = Math.hypot(dx, dz);
      const rise = top - prevBent.top;
      const midY = (proj.y + prevBent.ground) / 2 + Math.min(h, prevBent.top - prevBent.ground) * 0.5;
      const yaw = Math.atan2(dx, dz);
      for (const sign of [-1, 1] as const) {
        kit.add(
          pileMat,
          (nd.x + prevBent.x) / 2,
          midY,
          (nd.z + prevBent.z) / 2,
          yaw,
          0.14,
          0.14,
          Math.hypot(span, h),
          sign * Math.atan2(h * 0.8 + rise * 0.2, span),
        );
      }
    }
    prevBent = { x: nd.x, z: nd.z, top, ground: proj.y };
  }

  // The chute-end APRON: a sloped board plate capping the stub where the deck
  // hands off, plus the jetty's timber bull-nose across its lip.
  if (nStub > nEnd) {
    g.add(
      chStrip(ch.nodes, ch.step, nEnd, nStub, (nd) => [
        new THREE.Vector3(nd.x - nd.rightX * (half + 0.2), nd.y + 0.04, nd.z - nd.rightZ * (half + 0.2)),
        new THREE.Vector3(nd.x + nd.rightX * (half + 0.2), nd.y + 0.04, nd.z + nd.rightZ * (half + 0.2)),
      ], deckMat, 4),
    );
    const ne = ch.nodes[nStub];
    kit.add(beamMat, ne.x, ne.y + 0.06, ne.z, ne.dir, (half + 0.2) * 2, 0.3, 0.44);
  }

  // Landing apron at the catch: wider board lip + end beam + the faint warm
  // under-glow (the series' landing-invitation language, in lantern colour).
  const n0 = ch.nodes[0];
  g.add(
    chStrip(ch.nodes, ch.step, 0, 2, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * (half + 0.7), nd.y + 0.05, nd.z - nd.rightZ * (half + 0.7)),
      new THREE.Vector3(nd.x + nd.rightX * (half + 0.7), nd.y + 0.05, nd.z + nd.rightZ * (half + 0.7)),
    ], deckMat, 5),
  );
  g.add(box((half + 0.7) * 2, 0.7, 0.3, beamMat, n0.x, n0.y - 0.35, n0.z, n0.dir));
  const glow = new THREE.Mesh(
    new THREE.PlaneGeometry((half + 0.4) * 2, 1),
    new THREE.MeshBasicMaterial({
      color: p.accent,
      transparent: true,
      opacity: 0.12,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  glow.position.set(n0.x, n0.y - 0.8, n0.z);
  glow.rotation.y = n0.dir;
  g.add(glow);

  kit.flush(g);
  dressExit(g, sc, beamMat);
}

// ---- Entry ----------------------------------------------------------------

export function buildShortcutMeshes(
  track: Track,
  placed: readonly PlacedShortcut[],
  theme: TrackTheme,
  ramp?: { at: number; length: number; halfWidth: number; height: number },
): THREE.Group {
  void theme; // per-city tint hooks come with the design pass
  const group = new THREE.Group();
  for (const sc of placed) {
    const g = new THREE.Group();
    // Batch-2 dispatch: `look` (VISUAL identity, resolved by placeShortcuts)
    // routes to the per-city builder files; the batch-1 styles keep their
    // builders here. NYC's default look 'park' falls through to buildHighline.
    if (sc.style === 'cavern') buildCavern(track, sc, g);
    else if (sc.look === 'expressway') buildTokyoShortcut(track, sc, g);
    else if (sc.look === 'rooftop') buildNapoliShortcut(track, sc, g);
    else if (sc.style === 'highline') buildHighline(track, sc, g);
    else buildSkypath(track, sc, g);
    // M117c: a skypath's launcher is the CITY ramp — put the "speed boost on
    // the ramp" cue on the real incline.
    // M117f: it was a 2.4-u additive strip up the middle of the pier deck —
    // the brightest object for 60 u and pure runway. It is BRASS DECK STUDS
    // now (the studs language NYC and Tokyo share, in the pier's material),
    // set into the boards just off the centre line with a damp sheen patch
    // around them, so a player who knows to look still finds the line.
    if (sc.catch && ramp) {
      const start = ramp.at * track.finishDistance;
      const slope = ramp.height / ramp.length;
      const studMat = new THREE.MeshStandardMaterial({
        color: 0xd8b06a,
        roughness: 0.32,
        metalness: 0.5,
        emissive: new THREE.Color(0x7fd8ff),
        emissiveIntensity: 0.55,
      });
      const inst = new THREE.InstancedMesh(UNIT_BOX, studMat, 6);
      const mt = new THREE.Matrix4();
      const scv = new THREE.Vector3(0.4, 0.1, 0.3);
      for (let i = 0; i < 6; i++) {
        const t = 0.1 + (i / 5) * 0.8;
        const s = track.sampleAt(start + t * ramp.length);
        const lat = -0.9 + (art(i * 19 + 7) - 0.5) * 0.8;
        inst.setMatrixAt(
          i,
          mt.compose(
            new THREE.Vector3(s.x + s.rightX * lat, s.y + t * ramp.height + 0.05, s.z + s.rightZ * lat),
            new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.atan(slope), s.dir, 0, 'YXZ')),
            scv,
          ),
        );
      }
      inst.instanceMatrix.needsUpdate = true;
      g.add(inst);
      // NO sheen band under them: a 2-u damp streak was tried and it simply
      // rebuilt the runway stripe in a different colour (measured — it was the
      // brightest thing on the pier deck from the approach). The studs alone
      // are the cue, exactly as at NYC and Tokyo.
      // (The M99 incline-decal convention, if a decal is ever wanted here, is
      // rot.x = −π/2 − atan(slope); the negated form buries it in the deck.)
    }
    group.add(g);
  }
  return group;
}
